import { z } from 'zod';
import { sectionHasValue } from '../util';
import { registerEnum, registerEnumArray } from '../registered-enum';
import { registry } from '../registry';
export const baseWeightClinical = z
    .object({
    wnl: z.boolean().optional(),
    currentHeight: z.string().optional().register(registry, { label: 'Height (in)' }),
    currentWeight: z.string().optional().register(registry, { label: 'Weight (lbs)' }),
    heightSource: registerEnum([
        { value: 'measured', label: 'Measured' },
        { value: 'patientReported', label: 'Reported' },
        { value: 'priorRecord', label: 'Prior record' },
    ], { label: 'Height Source' }).optional(),
    weightSource: registerEnum([
        { value: 'measured', label: 'Measured' },
        { value: 'patientReported', label: 'Reported' },
    ], { label: 'Weight Source' }).optional(),
    previousWeight: z.string().optional().register(registry, { label: 'Previous Weight (lbs)' }),
    previousWeightDate: z.string().optional().register(registry, { label: 'Previous Weight Date' }),
    previousWeightSource: registerEnum([
        { value: 'lastVisit', label: 'Last visit' },
        { value: 'hospitalDC', label: 'Hospital DC' },
        { value: 'mdOffice', label: 'MD office' },
        { value: 'patientReport', label: 'Patient report' },
    ], { label: 'Previous Weight Source' }).optional(),
    weightChange: registerEnum([
        { value: 'stable', label: 'Stable' },
        { value: 'gained', label: 'Gained' },
        { value: 'lost', label: 'Lost' },
    ], { label: 'Weight Change' }).optional(),
    weightChangeAmount: z.string().optional().register(registry, { label: 'Weight Change Amount (lbs)' }),
    weightChangePeriod: registerEnum([
        { value: '1week', label: '1 week' },
        { value: '2weeks', label: '2 weeks' },
        { value: '1month', label: '1 month' },
        { value: 'other', label: 'Other' },
    ], { label: 'Weight Change Period' }).optional(),
    weightChangePeriodOther: z.string().optional().register(registry, { label: 'Weight Change Period Other' }),
    changeIntentional: registerEnum([
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
        { value: 'unknown', label: 'Unknown' },
    ], { label: 'Change Intentional' }).optional(),
    weightLossCauses: registerEnumArray([
        { value: 'decreasedAppetite', label: 'Decreased appetite' },
        { value: 'nauseaVomiting', label: 'Nausea/vomiting' },
        { value: 'difficultySwallowing', label: 'Difficulty swallowing' },
        { value: 'dentalProblems', label: 'Dental problems' },
        { value: 'depression', label: 'Depression' },
        { value: 'medicationSideEffect', label: 'Medication side effect' },
        { value: 'financialFoodAccess', label: 'Financial/food access' },
        { value: 'unableToPrepareMeals', label: 'Unable to prepare meals' },
        { value: 'illnessProgression', label: 'Illness/disease progression' },
        { value: 'unknown', label: 'Unknown' },
    ], { label: 'Unintentional Weight Loss Cause' }).optional(),
    weightGainCauses: registerEnumArray([
        { value: 'fluidRetention', label: 'Fluid retention' },
        { value: 'dietaryIndiscretion', label: 'Dietary indiscretion (sodium)' },
        { value: 'medicationNonCompliance', label: 'Medication non-compliance' },
        { value: 'diseaseProgression', label: 'Disease progression' },
        { value: 'medicationChange', label: 'Medication change' },
        { value: 'intentional', label: 'Intentional' },
        { value: 'unknown', label: 'Unknown' },
    ], { label: 'Weight Gain Cause' }).optional(),
    weightGainSymptoms: registerEnumArray([
        { value: 'increasedSOB', label: 'Increased SOB' },
        { value: 'increasedEdema', label: 'Increased edema' },
        { value: 'orthopnea', label: 'Orthopnea' },
        { value: 'pnd', label: 'PND' },
    ], { label: 'Weight Gain Symptoms' }).optional(),
    monitoringReasons: registerEnumArray([
        { value: 'chfManagement', label: 'CHF management' },
        { value: 'renalDisease', label: 'Renal disease' },
        { value: 'nutritionOptimization', label: 'Nutrition optimization' },
        { value: 'woundHealing', label: 'Wound healing' },
        { value: 'medicationManagement', label: 'Medication management' },
        { value: 'diseaseProgressionMonitoring', label: 'Disease progression monitoring' },
    ], { label: 'Weight Monitoring Reasons' }).optional(),
    clinicalNarrative: z.string().optional().register(registry, { label: 'Clinical Narrative' }),
})
    .register(registry, { label: 'Weight Clinical' });
export const weightClinicalHasValue = (data) => {
    const { currentHeight, currentWeight, ...clinicalValues } = data;
    return sectionHasValue(clinicalValues);
};
export const baseWeightClinicalRefined = baseWeightClinical.refine((data) => weightClinicalHasValue(data), {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
export const baseOralIntakeClinical = z.object({
    wnl: z.boolean().optional(),
    appetite: z
        .object({
        currentAppetite: registerEnum([
            { value: 'good', label: 'Good' },
            { value: 'fair', label: 'Fair' },
            { value: 'poor', label: 'Poor' },
            { value: 'anorexia', label: 'Anorexia' },
        ], { label: 'Current Appetite' }).optional(),
        appetiteChange: registerEnum([
            { value: 'unchanged', label: 'Unchanged' },
            { value: 'improved', label: 'Improved' },
            { value: 'decreased', label: 'Decreased' },
        ], { label: 'Appetite Change' }).optional(),
        appetiteChangeSince: z.string().optional().register(registry, { label: 'Appetite Change Since' }),
        appetiteChangeCause: z.string().optional().register(registry, { label: 'Appetite Change Cause' }),
        mealsPerDay: registerEnum([
            { value: '3meals', label: '3 meals' },
            { value: '2meals', label: '2 meals' },
            { value: '1meal', label: '1 meal' },
            { value: 'snacksOnly', label: 'Snacks only' },
        ], { label: 'Meals Per Day' }).optional(),
        typicalIntake: registerEnum([
            { value: '100', label: '100%' },
            { value: '75', label: '75%' },
            { value: '50', label: '50%' },
            { value: '25', label: '25%' },
            { value: 'less25', label: '<25%' },
        ], { label: 'Typical Intake Per Meal' }).optional(),
        fluidIntake: registerEnum([
            { value: 'adequate', label: 'Adequate (6+ glasses/day)' },
            { value: 'marginal', label: 'Marginal (4-6 glasses/day)' },
            { value: 'poor', label: 'Poor (<4 glasses/day)' },
            { value: 'restricted', label: 'Restricted' },
        ], { label: 'Fluid Intake' }).optional(),
    })
        .register(registry, { label: 'Appetite & Intake Pattern' })
        .optional(),
    swallowing: z
        .object({
        swallowingDifficulty: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'solids', label: 'With solids' },
            { value: 'liquids', label: 'With liquids' },
            { value: 'both', label: 'Both' },
            { value: 'pills', label: 'With pills' },
        ], { label: 'Swallowing Difficulty' }).optional(),
        aspirationRiskSigns: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'coughing', label: 'Coughing' },
            { value: 'choking', label: 'Choking' },
            { value: 'wetVoice', label: 'Wet voice' },
            { value: 'foodPocketing', label: 'Food pocketing' },
            { value: 'drooling', label: 'Drooling' },
            { value: 'multipleSwallows', label: 'Multiple swallows' },
            { value: 'prolongedChewing', label: 'Prolonged chewing' },
        ], { label: 'Signs of Aspiration Risk' }).optional(),
        aspirationPneumoniaHistory: registerEnum([
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
        ], { label: 'Aspiration Pneumonia History' }).optional(),
        aspirationPneumoniaWhen: z.string().optional().register(registry, { label: 'Aspiration Pneumonia When' }),
        aspirationPneumoniaEpisodes: z.string().optional().register(registry, { label: 'Aspiration Pneumonia Episodes' }),
        slpEvaluation: registerEnum([
            { value: 'notIndicated', label: 'Not indicated' },
            { value: 'recommended', label: 'Recommended' },
            { value: 'completed', label: 'Completed' },
            { value: 'refused', label: 'Refused' },
        ], { label: 'SLP Evaluation' }).optional(),
        slpEvaluationDate: z.string().optional().register(registry, { label: 'SLP Evaluation Date' }),
    })
        .register(registry, { label: 'Swallowing Assessment' })
        .optional(),
    factorsAffecting: z
        .object({
        physical: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'fatigue', label: 'Fatigue' },
            { value: 'weakness', label: 'Weakness' },
            { value: 'pain', label: 'Pain' },
            { value: 'sob', label: 'SOB' },
            { value: 'nausea', label: 'Nausea' },
            { value: 'vomiting', label: 'Vomiting' },
            { value: 'constipation', label: 'Constipation' },
            { value: 'diarrhea', label: 'Diarrhea' },
            { value: 'mouthSores', label: 'Mouth sores' },
            { value: 'dentalProblems', label: 'Dental problems' },
            { value: 'illFittingDentures', label: 'Ill-fitting dentures' },
        ], { label: 'Factors Affecting Nutrition - Physical' }).optional(),
        sensory: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'tasteChanges', label: 'Taste changes' },
            { value: 'smellChanges', label: 'Smell changes' },
            { value: 'visionImpairment', label: 'Vision impairment' },
            { value: 'unableToReadLabels', label: 'Unable to read labels' },
        ], { label: 'Factors Affecting Nutrition - Sensory' }).optional(),
        cognitivePsych: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'forgetsToEat', label: 'Forgets to eat' },
            { value: 'forgetsIfAte', label: 'Forgets if ate' },
            { value: 'depression', label: 'Depression' },
            { value: 'anxiety', label: 'Anxiety' },
            { value: 'confusionAboutDiet', label: 'Confusion about diet' },
        ], { label: 'Factors Affecting Nutrition - Cognitive/Psychological' }).optional(),
        functional: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'unableToShop', label: 'Unable to shop' },
            { value: 'unableToPrepareMeals', label: 'Unable to prepare meals' },
            { value: 'unableToOpenContainers', label: 'Unable to open containers' },
            { value: 'unableToCutFood', label: 'Unable to cut food' },
            { value: 'unableToFeedSelf', label: 'Unable to feed self' },
        ], { label: 'Factors Affecting Nutrition - Functional' }).optional(),
        medicationsAffecting: registerEnum([
            { value: 'none', label: 'None identified' },
            { value: 'yes', label: 'Yes' },
        ], { label: 'Medications Affecting Appetite' }).optional(),
        medicationsAffectingDetails: z.string().optional().register(registry, { label: 'Medications Affecting Appetite Details' }),
        socialEconomic: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'foodInsecurity', label: 'Food insecurity' },
            { value: 'limitedIncome', label: 'Limited income' },
            { value: 'eatsAlone', label: 'Eats alone' },
            { value: 'noOneToPrepareMeals', label: 'No one to prepare meals' },
        ], { label: 'Factors Affecting Nutrition - Social/Economic' }).optional(),
    })
        .register(registry, { label: 'Factors Affecting Nutrition' })
        .optional(),
});
export const baseEatingSupport = z.object({
    wnl: z.boolean().optional(),
    adaptiveEquipment: z
        .object({
        equipmentInUse: registerEnumArray([
            { value: 'noneNeeded', label: 'None needed' },
            { value: 'builtUpUtensils', label: 'Built-up utensils' },
            { value: 'weightedUtensils', label: 'Weighted utensils' },
            { value: 'rockerKnife', label: 'Rocker knife' },
            { value: 'plateGuard', label: 'Plate guard' },
            { value: 'scoopDish', label: 'Scoop dish' },
            { value: 'nonSlipMat', label: 'Non-slip mat' },
            { value: 'adaptiveCup', label: 'Adaptive cup/glass' },
            { value: 'straw', label: 'Straw' },
            { value: 'longStraw', label: 'Long straw' },
            { value: 'other', label: 'Other' },
        ], { label: 'Adaptive Equipment In Use' }).optional(),
        equipmentInUseOther: z.string().optional().register(registry, { label: 'Other Equipment' }),
        equipmentNeeded: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'list', label: 'List' },
            { value: 'otConsult', label: 'OT consult for equipment eval' },
        ], { label: 'Adaptive Equipment Needed' }).optional(),
        equipmentNeededList: z.string().optional().register(registry, { label: 'Equipment Needed List' }),
    })
        .register(registry, { label: 'Adaptive Equipment' })
        .optional(),
    mealAssistance: z
        .object({
        assistanceProvider: registerEnumArray([
            { value: 'noOneNeeded', label: 'No one needed' },
            { value: 'spouse', label: 'Spouse/partner' },
            { value: 'adultChild', label: 'Adult child' },
            { value: 'otherFamily', label: 'Other family' },
            { value: 'paidCaregiver', label: 'Paid caregiver' },
            { value: 'facilityStaff', label: 'Facility staff' },
            { value: 'mealsOnWheels', label: 'Meals on Wheels/delivered' },
        ], { label: 'Meal Assistance Provider' }).optional(),
        helpType: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'groceryShopping', label: 'Grocery shopping' },
            { value: 'mealPreparation', label: 'Meal preparation' },
            { value: 'setupAtTable', label: 'Setup at table' },
            { value: 'cuttingFood', label: 'Cutting food' },
            { value: 'openingContainers', label: 'Opening containers' },
            { value: 'supervisionCueing', label: 'Supervision/cueing' },
            { value: 'feeding', label: 'Feeding' },
        ], { label: 'Meal Assistance Help Type' }).optional(),
        caregiverTrainingNeeded: registerEnum([
            { value: 'no', label: 'No' },
            { value: 'yes', label: 'Yes' },
        ], { label: 'Caregiver Training Needed' }).optional(),
        caregiverTrainingTopics: registerEnumArray([
            { value: 'safeFeedingTechniques', label: 'Safe feeding techniques' },
            { value: 'aspirationPrecautions', label: 'Aspiration precautions' },
            { value: 'dietPreparation', label: 'Diet preparation' },
            { value: 'tubeFeeding', label: 'Tube feeding' },
            { value: 'other', label: 'Other' },
        ], { label: 'Caregiver Training Topics' }).optional(),
        caregiverTrainingOther: z.string().optional().register(registry, { label: 'Other Training' }),
    })
        .register(registry, { label: 'Meal Assistance' })
        .optional(),
});
export const basePocNutrition = z
    .object({
    skilledJustification: registerEnumArray([
        {
            value: 'weightMonitoring',
            label: 'Weight monitoring with clinical correlation (CHF, renal, wound healing, disease progression)',
        },
        {
            value: 'tubeFeedingManagement',
            label: 'Tube feeding management (rate/formula adjustments, residual checks, complication assessment)',
        },
        {
            value: 'tpnManagement',
            label: 'TPN/IV nutrition management (access site care, complication monitoring, lab correlation)',
        },
        { value: 'dysphagiaMonitoring', label: 'Dysphagia monitoring and aspiration precaution implementation' },
        {
            value: 'therapeuticDietEducation',
            label: 'Therapeutic diet education and compliance monitoring with clinical outcome correlation',
        },
        { value: 'woundHealingNutrition', label: 'Nutrition assessment for wound healing optimization' },
        { value: 'bloodGlucoseCorrelation', label: 'Blood glucose correlation with nutritional intake (diabetic management)' },
        { value: 'electrolyteCorrelation', label: 'Electrolyte/lab correlation with nutritional status' },
        {
            value: 'patientCaregiverEducation',
            label: 'Patient/caregiver education requiring skilled teaching (complex diet, tube feeding, TPN)',
        },
        { value: 'barrierManagement', label: 'Identification and management of barriers to adequate nutrition' },
    ], { label: 'POC Nutrition Skilled Justification' }).optional(),
})
    .register(registry, { label: 'POC Nutrition' });
export const baseK0520Clinical = z.object({
    wnl: z.boolean().optional(),
    parenteral: z
        .object({
        type: registerEnumArray([
            { value: 'tpn', label: 'TPN' },
            { value: 'ppn', label: 'PPN' },
            { value: 'ivFluids', label: 'IV fluids for hydration' },
        ], { label: 'Parenteral Type' }).optional(),
        access: registerEnumArray([
            { value: 'picc', label: 'PICC' },
            { value: 'centralLine', label: 'Central line' },
            { value: 'port', label: 'Port' },
            { value: 'peripheralIv', label: 'Peripheral IV' },
        ], { label: 'Parenteral Access' }).optional(),
        formula: z.string().optional().register(registry, { label: 'Formula' }),
        rate: z.string().optional().register(registry, { label: 'Rate (ml/hr)' }),
        hoursPerDay: z.string().optional().register(registry, { label: 'Hours/day' }),
        caloriesPerDay: z.string().optional().register(registry, { label: 'Calories/day' }),
        monitoring: registerEnumArray([
            { value: 'dailyWeights', label: 'Daily weights' },
            { value: 'io', label: 'I&O' },
            { value: 'labs', label: 'Labs (glucose, electrolytes, LFTs)' },
        ], { label: 'Parenteral Monitoring' }).optional(),
        monitoringFrequency: z.string().optional().register(registry, { label: 'Monitoring Frequency' }),
        complications: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'lineInfection', label: 'Line infection' },
            { value: 'hyperglycemia', label: 'Hyperglycemia' },
            { value: 'electrolyteImbalance', label: 'Electrolyte imbalance' },
            { value: 'fluidOverload', label: 'Fluid overload' },
        ], { label: 'Parenteral Complications' }).optional(),
    })
        .register(registry, { label: 'Parenteral/IV Feeding' })
        .optional(),
    feedingTube: z
        .object({
        tubeType: registerEnumArray([
            { value: 'ng', label: 'NG' },
            { value: 'gTube', label: 'G-tube/PEG' },
            { value: 'jTube', label: 'J-tube' },
            { value: 'gjTube', label: 'G-J tube' },
        ], { label: 'Feeding Tube Type' }).optional(),
        size: z.string().optional().register(registry, { label: 'Size (Fr)' }),
        placedDate: z.string().optional().register(registry, { label: 'Placed Date' }),
        formula: z.string().optional().register(registry, { label: 'Formula' }),
        rate: z.string().optional().register(registry, { label: 'Rate (ml/hr)' }),
        method: registerEnumArray([
            { value: 'continuousPump', label: 'Continuous pump' },
            { value: 'bolus', label: 'Bolus' },
            { value: 'gravity', label: 'Gravity' },
        ], { label: 'Feeding Tube Method' }).optional(),
        schedule: z.string().optional().register(registry, { label: 'Schedule' }),
        totalVolumePerDay: z.string().optional().register(registry, { label: 'Total Volume Per Day (ml)' }),
        freeWaterFlushes: z.string().optional().register(registry, { label: 'Free Water Flushes (ml)' }),
        tubeSite: registerEnumArray([
            { value: 'cleanDryIntact', label: 'Clean/dry/intact' },
            { value: 'redness', label: 'Redness' },
            { value: 'drainage', label: 'Drainage' },
            { value: 'granulationTissue', label: 'Granulation tissue' },
            { value: 'leakage', label: 'Leakage' },
            { value: 'skinBreakdown', label: 'Skin breakdown' },
        ], { label: 'Tube Site Assessment' }).optional(),
        residuals: z.string().optional().register(registry, { label: 'Residuals (ml)' }),
        residualThreshold: z.string().optional().register(registry, { label: 'Residual Threshold (ml)' }),
        residualAction: registerEnumArray([
            { value: 'recheckIn1Hr', label: 'Recheck in 1 hr' },
            { value: 'holdFeeding', label: 'Hold feeding' },
            { value: 'notifyMd', label: 'Notify MD' },
        ], { label: 'Action if Residuals High' }).optional(),
        patientCompetency: registerEnum([
            { value: 'independent', label: 'Independent' },
            { value: 'needsSupervision', label: 'Needs supervision' },
            { value: 'needsAssistance', label: 'Needs assistance' },
            { value: 'unable', label: 'Unable (nursing does all)' },
        ], { label: 'Patient/CG Competency' }).optional(),
        skilledJustification: registerEnumArray([
            { value: 'siteAssessment', label: 'Site assessment' },
            { value: 'patencyVerification', label: 'Patency verification' },
            { value: 'residualMonitoring', label: 'Residual monitoring' },
            { value: 'rateFormulaAdjustments', label: 'Rate/formula adjustments' },
            { value: 'complicationIdentification', label: 'Complication identification' },
            { value: 'patientCaregiverEducation', label: 'Patient/caregiver education' },
        ], { label: 'Feeding Tube Skilled Justification' }).optional(),
    })
        .register(registry, { label: 'Feeding Tube' })
        .optional(),
    mechanicallyAlteredDiet: z
        .object({
        foodTexture: registerEnumArray([
            { value: 'regular', label: 'Regular' },
            { value: 'soft', label: 'Soft' },
            { value: 'chopped', label: 'Chopped' },
            { value: 'ground', label: 'Ground' },
            { value: 'pureed', label: 'Pureed' },
            { value: 'npo', label: 'NPO' },
        ], { label: 'Food Texture' }).optional(),
        liquidConsistency: registerEnumArray([
            { value: 'thin', label: 'Thin' },
            { value: 'nectarThick', label: 'Nectar-thick' },
            { value: 'honeyThick', label: 'Honey-thick' },
            { value: 'puddingThick', label: 'Pudding-thick' },
            { value: 'npoLiquids', label: 'NPO to liquids' },
        ], { label: 'Liquid Consistency' }).optional(),
        reason: registerEnumArray([
            { value: 'dysphagia', label: 'Dysphagia' },
            { value: 'aspirationRisk', label: 'Aspiration risk' },
            { value: 'chewingDifficulty', label: 'Chewing difficulty' },
            { value: 'dentalProblems', label: 'Dental problems' },
            { value: 'postSurgical', label: 'Post-surgical' },
            { value: 'other', label: 'Other' },
        ], { label: 'Mechanically Altered Diet Reason' }).optional(),
        reasonOther: z.string().optional().register(registry, { label: 'Other Reason' }),
        swallowEval: registerEnum([
            { value: 'none', label: 'None' },
            { value: 'bedsideSlp', label: 'Bedside by SLP' },
            { value: 'modifiedBarium', label: 'Modified barium swallow' },
        ], { label: 'Swallow Evaluation' }).optional(),
        swallowEvalDate: z.string().optional().register(registry, { label: 'Swallow Evaluation Date' }),
        dietCompliance: registerEnum([
            { value: 'followsConsistently', label: 'Follows consistently' },
            { value: 'occasionalDeviations', label: 'Occasional deviations' },
            { value: 'frequentlyNonCompliant', label: 'Frequently non-compliant' },
            { value: 'refuses', label: 'Refuses' },
        ], { label: 'Mechanically Altered Diet Compliance' }).optional(),
    })
        .register(registry, { label: 'Mechanically Altered Diet' })
        .optional(),
    therapeuticDiet: z
        .object({
        dietTypes: registerEnumArray([
            { value: 'diabetic', label: 'Diabetic/carb controlled' },
            { value: 'cardiac', label: 'Cardiac/heart healthy' },
            { value: 'lowSodium', label: 'Low sodium' },
            { value: 'renal', label: 'Renal' },
            { value: 'lowPotassium', label: 'Low potassium' },
            { value: 'lowPhosphorus', label: 'Low phosphorus' },
            { value: 'lowFat', label: 'Low fat' },
            { value: 'highProtein', label: 'High protein' },
            { value: 'fluidRestriction', label: 'Fluid restriction' },
            { value: 'other', label: 'Other' },
        ], { label: 'Therapeutic Diet Types' }).optional(),
        carbsPerMeal: z.string().optional().register(registry, { label: 'Carbs Per Meal (g)' }),
        sodiumPerDay: z.string().optional().register(registry, { label: 'Sodium Per Day (mg)' }),
        fluidRestriction: z.string().optional().register(registry, { label: 'Fluid Restriction (ml/day)' }),
        dietTypeOther: z.string().optional().register(registry, { label: 'Other Diet Type' }),
        prescribedBy: registerEnumArray([
            { value: 'physician', label: 'Physician' },
            { value: 'dietitian', label: 'Dietitian' },
        ], { label: 'Therapeutic Diet Prescribed By' }).optional(),
        educationProvided: registerEnum([
            { value: 'yes', label: 'Yes' },
            { value: 'needsReinforcement', label: 'Needs reinforcement' },
        ], { label: 'Education Provided' }).optional(),
        educationDate: z.string().optional().register(registry, { label: 'Education Date' }),
        dietCompliance: registerEnum([
            { value: 'followsConsistently', label: 'Follows consistently' },
            { value: 'partialCompliance', label: 'Partial compliance' },
            { value: 'poorCompliance', label: 'Poor compliance' },
            { value: 'refuses', label: 'Refuses' },
        ], { label: 'Therapeutic Diet Compliance' }).optional(),
        complianceBarriers: registerEnumArray([
            { value: 'none', label: 'None' },
            { value: 'knowledgeDeficit', label: 'Knowledge deficit' },
            { value: 'cost', label: 'Cost' },
            { value: 'foodPrepDifficulty', label: 'Food preparation difficulty' },
            { value: 'culturalPreference', label: 'Cultural/preference' },
            { value: 'cognitive', label: 'Cognitive' },
            { value: 'caregiverNotFollowing', label: 'Caregiver not following' },
        ], { label: 'Therapeutic Diet Compliance Barriers' }).optional(),
        skilledJustification: registerEnumArray([
            { value: 'complianceAssessment', label: 'Compliance assessment' },
            { value: 'bloodSugarCorrelation', label: 'Blood sugar correlation with diet' },
            { value: 'fluidSodiumCorrelation', label: 'Fluid/sodium correlation with weight/edema' },
            { value: 'labValueCorrelation', label: 'Lab value correlation' },
            { value: 'patientCaregiverEducation', label: 'Patient/caregiver education' },
            { value: 'dietaryModifications', label: 'Dietary modifications' },
        ], { label: 'Therapeutic Diet Skilled Justification' }).optional(),
    })
        .register(registry, { label: 'Therapeutic Diet' })
        .optional(),
});
